// AddLeaveMaster.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AddLeaveMaster = ({ show, onHide, employee, leaveTypes, onLeaveMasterAdded }) => {
  const [formData, setFormData] = useState({
    leave_type_id: '',
    leave_count: '',
    frequency: 'Yearly',
    credited_daymonth: '1',
    is_paid: 1,
    carry_forward: 0,
    status: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  const API_BASE = process.env.REACT_APP_API_BASE;

  useEffect(() => {
    if (show && employee) {
      resetForm();
    }
  }, [show, employee]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value
    }));

    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.leave_type_id) {
      errors.leave_type_id = 'Leave type is required';
    }

    if (!formData.leave_count.trim()) {
      errors.leave_count = 'Leave count is required';
    } else {
      const numValue = parseFloat(formData.leave_count);
      if (isNaN(numValue)) {
        errors.leave_count = 'Please enter a valid number';
      } else if (numValue <= 0) {
        errors.leave_count = 'Leave count must be greater than 0';
      }
    }

    if (!formData.credited_daymonth.trim()) {
      errors.credited_daymonth = 'Credit day/month is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setError('Please fix the validation errors');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('authToken');
      const leaveMasterData = {
        emp_id: employee.emp_id,
        ...formData
      };

      const response = await axios.post(`${API_BASE}leave/add_leave_master.php`,
        leaveMasterData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        onLeaveMasterAdded();
        onHide();
        resetForm();
      } else {
        setError(response.data.error || 'Failed to add leave allocation');
      }
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to add leave allocation');
      console.error('Error adding leave allocation:', error);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      leave_type_id: '',
      leave_count: '',
      frequency: 'Yearly',
      credited_daymonth: '1',
      is_paid: 1,
      carry_forward: 0,
      status: 1
    });
    setValidationErrors({});
    setError('');
  };

  const handleCancel = () => {
    resetForm();
    onHide();
  };

  if (!show) return null;

  return (
    <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', overflowY: 'auto' }}>
      <div className="modal-dialog modal-lg modal-dialog-centered">
        <div className="modal-content">
          <div className="modal-header" style={{
            background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
            borderBottom: 'none'
          }}>
            <h5 className="modal-title text-white">
              <i className="bi bi-calendar-plus me-2"></i>
              Add Leave Allocation
            </h5>
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={handleCancel}
              disabled={loading}
            ></button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {error && (
                <div className="alert alert-danger alert-dismissible fade show" role="alert">
                  <i className="bi bi-exclamation-triangle-fill me-2"></i>
                  {error}
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setError('')}
                  ></button>
                </div>
              )}

              {/* Employee Info */}
              <div className="card border-0 bg-light mb-4">
                <div className="card-body">
                  <h6 className="fw-bold text-success mb-3">
                    <i className="bi bi-person me-2"></i>
                    Employee Information
                  </h6>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted d-block">Employee Name</small>
                        <strong>{employee.first_name} {employee.last_name}</strong>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">Employee Code</small>
                        <strong>{employee.emp_code}</strong>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="mb-2">
                        <small className="text-muted d-block">Email</small>
                        <strong>{employee.email || 'N/A'}</strong>
                      </div>
                      <div className="mb-2">
                        <small className="text-muted d-block">Status</small>
                        <span className={`badge ${employee.status === 'Active' ? 'bg-success' :
                          employee.status === 'Inactive' ? 'bg-secondary' :
                            'bg-danger'
                          }`}>
                          {employee.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Form Fields */}
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="leave_type_id" className="form-label fw-medium">
                      Leave Type <span className="text-danger">*</span>
                    </label>
                    <select
                      className={`form-select ${validationErrors.leave_type_id ? 'is-invalid' : ''}`}
                      id="leave_type_id"
                      name="leave_type_id"
                      value={formData.leave_type_id}
                      onChange={handleChange}
                      required
                      disabled={loading}
                    >
                      <option value="">Select a leave type</option>
                      {leaveTypes.map(leaveType => (
                        <option key={leaveType.leave_type_id} value={leaveType.leave_type_id}>
                          {leaveType.leave_name} ({leaveType.leave_code})
                          {leaveType.annual_limit && ` - Annual Limit: ${leaveType.annual_limit} days`}
                        </option>
                      ))}
                    </select>
                    {validationErrors.leave_type_id && (
                      <div className="invalid-feedback">{validationErrors.leave_type_id}</div>
                    )}
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="leave_count" className="form-label fw-medium">
                      Leave Count (days) <span className="text-danger">*</span>
                    </label>
                    <input
                      type="number"
                      className={`form-control ${validationErrors.leave_count ? 'is-invalid' : ''}`}
                      id="leave_count"
                      name="leave_count"
                      value={formData.leave_count}
                      onChange={handleChange}
                      placeholder="Enter number of days"
                      step="0.5"
                      min="0.5"
                      required
                      disabled={loading}
                    />
                    {validationErrors.leave_count && (
                      <div className="invalid-feedback">{validationErrors.leave_count}</div>
                    )}
                    <div className="form-text">
                      Enter the number of leave days allocated
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label className="form-label fw-medium">Frequency</label>
                    <div className="btn-group w-100" role="group">
                      <input
                        type="radio"
                        className="btn-check"
                        name="frequency"
                        id="frequency_yearly"
                        value="Yearly"
                        checked={formData.frequency === 'Yearly'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-primary" htmlFor="frequency_yearly">
                        Yearly
                      </label>

                      <input
                        type="radio"
                        className="btn-check"
                        name="frequency"
                        id="frequency_monthly"
                        value="Monthly"
                        checked={formData.frequency === 'Monthly'}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="btn btn-outline-info" htmlFor="frequency_monthly">
                        Monthly
                      </label>
                    </div>
                  </div>
                </div>

                <div className="col-md-6">
                  <div className="mb-3">
                    <label htmlFor="credited_daymonth" className="form-label fw-medium">
                      Credited Day/Month <span className="text-danger">*</span>
                    </label>
                    <input
                      type="text"
                      className={`form-control ${validationErrors.credited_daymonth ? 'is-invalid' : ''}`}
                      id="credited_daymonth"
                      name="credited_daymonth"
                      value={formData.credited_daymonth}
                      onChange={handleChange}
                      placeholder="e.g., 1 (for 1st of month) or Jan 1"
                      required
                      disabled={loading}
                    />
                    {validationErrors.credited_daymonth && (
                      <div className="invalid-feedback">{validationErrors.credited_daymonth}</div>
                    )}
                    <div className="form-text">
                      {formData.frequency === 'Yearly'
                        ? 'Day and month when leaves are credited yearly (e.g., "Jan 1" or "1")'
                        : 'Day of month when leaves are credited monthly (e.g., "1" for 1st of every month)'
                      }
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col-md-4">
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="is_paid"
                        name="is_paid"
                        checked={formData.is_paid === 1}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="form-check-label fw-medium" htmlFor="is_paid">
                        Paid Leave
                      </label>
                      <div className="form-text">
                        Whether leave is paid or unpaid
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="carry_forward"
                        name="carry_forward"
                        checked={formData.carry_forward === 1}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="form-check-label fw-medium" htmlFor="carry_forward">
                        Allow Carry Forward
                      </label>
                      <div className="form-text">
                        Allow unused leaves to carry forward
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="status"
                        name="status"
                        checked={formData.status === 1}
                        onChange={handleChange}
                        disabled={loading}
                      />
                      <label className="form-check-label fw-medium" htmlFor="status">
                        Active Allocation
                      </label>
                      <div className="form-text">
                        Inactive allocations won't be considered
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer" style={{
              background: 'linear-gradient(135deg, rgba(40, 167, 69, 0.05) 0%, rgba(32, 201, 151, 0.05) 100%)',
              borderTop: '1px solid rgba(40, 167, 69, 0.1)'
            }}>
              <button
                type="button"
                className="btn btn-light border-secondary-subtle"
                onClick={handleCancel}
                disabled={loading}
              >
                <i className="bi bi-x-circle me-2"></i>
                Cancel
              </button>
              <button
                type="submit"
                className="btn text-white border-0 shadow-sm"
                disabled={loading}
                style={{
                  background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  transition: 'all 0.3s',
                  padding: '0.5rem 1.5rem'
                }}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Adding...
                  </>
                ) : (
                  <>
                    <i className="bi bi-plus-circle me-2"></i>
                    Add Allocation
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AddLeaveMaster;